import apiClient from '@/services/api';

export interface CohortListRes {
    status: number;
    error: boolean;
    message: string;
    data: CohortListData;
}

export interface CohortListData {
    data: CohortDatum[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export interface CohortDatum {
    id: string;
    course_id: string;
    cohort_name: string;
    generated_cohort_title: string;
    description: string;
    start_date: string;
    registration_start: string;
    registration_end: string;
    duration: number;
    status: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
    created_by: string;
    created_at: string;
    updated_at: string;
    handbook_path?: string | null;
    module_template_ids?: string[];
    /** "CLASSIC" (Cohort 1 layout, default) | "JOURNEY" (Cohort 2+ Student Journey). */
    student_portal_version?: 'CLASSIC' | 'JOURNEY';
    course?: {
        course_name: string;
    };
}

export interface DayRecording {
    path: string;
    name: string;
}

export interface DayStudentLink {
    enrollment_id: string;
    link: string;
    meeting_time?: string;
    description?: string;
}

export interface Day {
    day_number: number;
    date: string;
    title: string;
    description: string | null;
    link: string;
    recorded_video: string | null;
    recorded_video_path?: string | null;
    recorded_video_name?: string | null;
    recordings?: DayRecording[];
    class_time?: string | null;
    is_link_active?: boolean;
    attachments: string[];
    is_active?: boolean;
    is_mentor_session?: boolean;
    student_links?: DayStudentLink[];
}

export interface CohortWeek {
    id: string;
    cohort_id: string;
    week_number: number;
    title?: string | null;
    days: Day[];
    is_active?: boolean;
    created_by: string;
    created_at: string;
    updated_at: string;
}

export interface CreateCohortPayload {
    course_id: string;
    cohort_name: string;
    description: string;
    start_date: string;
    registration_start: string;
    registration_end: string;
    duration?: number;
    status?: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
    handbook_path?: string;
    module_template_ids?: string[];
    student_portal_version?: 'CLASSIC' | 'JOURNEY';
    weeks: {
        week_number: number;
        title?: string;
        is_active?: boolean;
        days: {
            day_number: number;
            title: string;
            description?: string;
            link: string;
            recorded_video?: string;
            attachments?: string[];
            is_active?: boolean;
            is_mentor_session?: boolean;
            student_links?: DayStudentLink[];
        }[];
    }[];
}

export interface UpdateCohortPayload {
    course_id?: string;
    cohort_name?: string;
    description?: string;
    start_date?: string;
    registration_start?: string;
    registration_end?: string;
    duration?: number;
    status?: 'ACTIVE' | 'INACTIVE' | 'COMPLETED';
    handbook_path?: string;
    module_template_ids?: string[];
    student_portal_version?: 'CLASSIC' | 'JOURNEY';
}

export interface GetCohortRes {
    status: number;
    error: boolean;
    message: string;
    data: CohortDatum;
}

export interface GetScheduleRes {
    status: number;
    error: boolean;
    message: string;
    data: CohortWeek[];
}

const cohortsService = {
    getCohorts: async (
        page = 1,
        limit = 10,
        search?: string,
        courseId?: string,
        status?: string,
    ): Promise<CohortListRes> => {
        const res = await apiClient.get<CohortListRes>('/cohorts', {
            params: {
                page,
                limit,
                ...(search && search.trim() !== '' ? { search: search.trim() } : {}),
                ...(courseId ? { course_id: courseId } : {}),
                ...(status ? { status } : {}),
            },
        });
        return res.data;
    },

    createCohort: async (payload: CreateCohortPayload): Promise<GetCohortRes> => {
        const res = await apiClient.post<GetCohortRes>('/cohorts', payload);
        return res.data;
    },

    updateCohort: async (id: string, payload: UpdateCohortPayload): Promise<GetCohortRes> => {
        const res = await apiClient.patch<GetCohortRes>(`/cohorts/${id}`, payload);
        return res.data;
    },

    getCohort: async (id: string): Promise<GetCohortRes> => {
        const res = await apiClient.get<GetCohortRes>(`/cohorts/${id}`);
        return res.data;
    },

    deleteCohort: async (id: string): Promise<{ status: number; error: boolean; message: string; data: null }> => {
        const res = await apiClient.delete<{ status: number; error: boolean; message: string; data: null }>(`/cohorts/${id}`);
        return res.data;
    },

    getSchedule: async (cohortId: string): Promise<GetScheduleRes> => {
        const res = await apiClient.get<GetScheduleRes>(`/cohorts/${cohortId}/schedule`);
        return res.data;
    },

    updateScheduleWeek: async (cohortId: string, weekNumber: number, days: Day[], title?: string): Promise<GetScheduleRes> => {
        const res = await apiClient.put<GetScheduleRes>(`/cohorts/${cohortId}/schedule/week/${weekNumber}`, { days, title });
        return res.data;
    },

    updateScheduleVisibility: async (
        cohortId: string,
        weekNumber: number,
        payload: { week_is_active?: boolean; days?: { day_number: number; is_active: boolean }[] },
    ): Promise<GetScheduleRes> => {
        const res = await apiClient.patch<GetScheduleRes>(`/cohorts/${cohortId}/schedule/week/${weekNumber}/visibility`, payload);
        return res.data;
    },

    uploadFile: async (file: File, customName?: string): Promise<{ status: number; error: boolean; message: string; data: { filePath: string; fileName?: string; fileSize?: string } }> => {
        const formData = new FormData();
        formData.append('file', file);
        const url = customName ? `/cohorts/upload?customName=${encodeURIComponent(customName)}` : '/cohorts/upload';
        const res = await apiClient.post<{ status: number; error: boolean; message: string; data: { filePath: string; fileName?: string; fileSize?: string } }>(
            url,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 1800000,
            }
        );
        return res.data;
    },

    uploadVideo: async (file: File, customName?: string): Promise<{ status: number; error: boolean; message: string; data: { filePath: string; fileName?: string; fileSize?: string } }> => {
        const formData = new FormData();
        formData.append('file', file);
        const url = customName ? `/cohorts/upload-video?customName=${encodeURIComponent(customName)}` : '/cohorts/upload-video';
        const res = await apiClient.post<{ status: number; error: boolean; message: string; data: { filePath: string; fileName?: string; fileSize?: string } }>(
            url,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
                timeout: 1800000,
            }
        );
        return res.data;
    },

    getEnrolledStudents: async (cohortId: string): Promise<any> => {
        const res = await apiClient.get<any>(`/cohorts/${cohortId}/students`);
        return res.data;
    },

    getAttendance: async (cohortId: string, date: string): Promise<any> => {
        const res = await apiClient.get<any>(`/cohorts/${cohortId}/attendance/${date}`);
        return res.data;
    },

    saveAttendance: async (cohortId: string, date: string, records: { enrollment_id: string, status: string }[]): Promise<any> => {
        const res = await apiClient.post<any>(`/cohorts/${cohortId}/attendance`, { date, records });
        return res.data;
    },
};

export default cohortsService;
